import { FC, useContext, useEffect, useMemo, useCallback } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import {
  createResponseContext,
  getWebSocketUrl,
  initialQueryResponse,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
} from '../../../../../../_metronic/helpers'
import { useQueryRequest } from './QueryRequestProvider'
import { AtendimentoList } from '../../../atendimento/core/_models'
import { getAtendimentosMedicoLogado } from '../../../atendimento/core/_requests'
import { useAuth } from '../../../../auth'
import { Stomp } from '@stomp/stompjs'
import { useModal } from '../../../../../components'

const QueryResponseContext = createResponseContext<AtendimentoList>(initialQueryResponse)

const SOCKET_URL = getWebSocketUrl('/socket/atendimento')

export const QueryResponseProvider: FC<WithChildren> = ({ children }) => {
  const { isOpen } = useModal()
  const { auth } = useAuth()
  const { state } = useQueryRequest()

  const queryClient = useQueryClient()

  const query = useMemo(() => stringifyRequestQuery(state), [state])

  const queryKey = useMemo(() => [QUERIES.ATENDIMENTO_MEDICO_LIST, query, isOpen], [query, isOpen])

  const {
    isFetching,
    isError,
    refetch,
    data: response,
  } = useQuery(queryKey, () => getAtendimentosMedicoLogado(query), {
    cacheTime: 0,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    enabled: !isOpen,
  })

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries(queryKey)
  }, [queryClient, queryKey])

  useEffect(() => {
    const stompClient = Stomp.client(SOCKET_URL)

    const onConnect = (frame: string) => {
      console.log('Connected: ' + frame)
      stompClient.subscribe('/topic/atendimento', () => {
        invalidateQuery()
      })
    }

    stompClient.connect({}, onConnect)

    return () => {
      stompClient.disconnect(() => console.log('Disconnected'))
    }
  }, [auth?.accessToken, invalidateQuery])

  return (
    <QueryResponseContext.Provider
      value={{
        isLoading: isFetching,
        isError,
        refetch,
        response,
        query,
      }}
    >
      {children}
    </QueryResponseContext.Provider>
  )
}

export const useQueryResponse = () => useContext(QueryResponseContext)
